import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Sun, Moon, Monitor, MessageSquare, LogOut, Building2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSignature } from '../../contexts/SignatureContext';
import { ModePreference } from '../../theme/signatureTokens';
import { useTranslation } from 'react-i18next';
import { GalateaIAButton } from '../ui/GalateaIAButton';
import { getTenantLogoUrl, getGalateaLogoUrl } from '../../utils/tenantLogo';

export function Header() {
  const navigate = useNavigate();
  const { user, logout, getEmpresaId, getEmpresaNome, getEmpresaCnpj, getUserContext } = useAuth();
  const { mode, modePref, setModePreference, setLocale, locale } = useSignature();
  const { i18n } = useTranslation();
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const userContext = getUserContext();
  const empresaId = getEmpresaId();
  const empresaNome = getEmpresaNome();
  const empresaCnpj = getEmpresaCnpj();

  const logoUrl = getTenantLogoUrl(userContext || { empresa_id: empresaId, empresa_nome: empresaNome });

  const nomeUsuario = user?.name || user?.email?.split('@')[0] || 'Usuário';
  const inicialUsuario = nomeUsuario.charAt(0).toUpperCase();

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    setLocale(lang);
    setLangMenuOpen(false);
  };

  // Ciclo oficial de luminosidade: Claro -> Escuro -> Auto (segue o sistema)
  const nextModePref: ModePreference = modePref === 'light' ? 'dark' : modePref === 'dark' ? 'auto' : 'light';
  const modeButtonTitle =
    modePref === 'light'
      ? 'Tema: Claro — clique para Escuro'
      : modePref === 'dark'
        ? 'Tema: Escuro — clique para Auto (segue o sistema)'
        : `Tema: Auto (${mode === 'dark' ? 'Escuro' : 'Claro'} pelo sistema) — clique para Claro`;

  return (
    <header className="flex flex-col sticky top-0 z-50 transition-colors duration-200">
      {/* Header Principal */}
      {/* Altura fixada em px para casar com HEADER_HEIGHT_PX (constants/layout.ts).
          h-14/h-16 dependem do root font-size (14px) e nao entregam os 64px do
          design system, deixando a ActionBar (fixed top:64px) desalinhada. */}
      <div className="h-[64px] bg-[#17263B] border-b border-slate-800 text-white flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          {/* Marca Galatea Soberana (asset oficial: public/logo-galatea.png) */}
          <div className="flex items-center gap-2 pr-3">
            {/* Chip claro: a marca oficial é azul escura e não tem contraste sobre o header navy */}
            <div className="flex items-center rounded-lg bg-white px-2.5 py-1 shadow-sm">
              <img
                src={getGalateaLogoUrl()}
                alt="Galatea"
                className="h-8 w-auto object-contain cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>
          </div>

          {/* Logo do Tenant / Empresa */}
          <div className="flex items-center gap-3 border-l border-slate-800 pl-3 min-w-0">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt={empresaNome || 'Logo do Tenant'} 
                className="w-8 h-8 object-contain rounded bg-white p-0.5 shadow-sm shrink-0 border border-slate-700" 
              />
            ) : (
              <Building2 className="w-6 h-6 text-cyan-400 shrink-0" />
            )}
            <div className="flex flex-col max-w-[180px] min-w-0">
              <span className="text-xs font-bold tracking-wide text-slate-100 truncate" title={empresaNome || undefined}>
                {empresaNome || (empresaId ? `Empresa ${empresaId}` : 'Sem empresa')}
              </span>
              <span className="text-[10px] text-slate-400 font-mono truncate" title={empresaCnpj || undefined}>
                {empresaCnpj ? `CNPJ: ${empresaCnpj}` : (empresaId ? `ID: ${empresaId}` : '—')}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 text-slate-300">
            {/* Seletor de Idioma / Locale (6 Locales Suportados) */}
            <div className="relative">
              <button 
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="px-2.5 py-1 rounded-lg hover:bg-slate-800 text-slate-300 hover:text-white cursor-pointer transition-all flex items-center gap-1.5 border border-slate-800"
                title="Idioma e Região"
              >
                <Globe className="w-4 h-4 text-cyan-400" />
                <span className="text-xs font-bold uppercase tracking-wider">{i18n.language || locale || 'pt-BR'}</span>
              </button>
              
              {langMenuOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setLangMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-48 bg-[#0F172A] border border-slate-700/80 rounded-xl shadow-2xl z-40 py-1.5 text-xs text-slate-200 divide-y divide-slate-800/60">
                    <div className="px-3 py-1 font-bold text-[10px] uppercase text-slate-500 tracking-wider">Locales Suportados</div>
                    <div className="py-1">
                      <button 
                        onClick={() => handleLanguageChange('pt-BR')}
                        className={`w-full text-left px-3 py-1.5 hover:bg-slate-800 flex items-center gap-2 cursor-pointer ${i18n.language === 'pt-BR' ? 'text-cyan-400 font-bold bg-slate-800/50' : ''}`}
                      >
                        <span>🇧🇷</span> Português (Brasil)
                      </button>
                      <button 
                        onClick={() => handleLanguageChange('es-UY')}
                        className={`w-full text-left px-3 py-1.5 hover:bg-slate-800 flex items-center gap-2 cursor-pointer ${i18n.language === 'es-UY' ? 'text-cyan-400 font-bold bg-slate-800/50' : ''}`}
                      >
                        <span>🇺🇾</span> Español (Uruguay)
                      </button>
                      <button 
                        onClick={() => handleLanguageChange('es-PY')}
                        className={`w-full text-left px-3 py-1.5 hover:bg-slate-800 flex items-center gap-2 cursor-pointer ${i18n.language === 'es-PY' ? 'text-cyan-400 font-bold bg-slate-800/50' : ''}`}
                      >
                        <span>🇵🇾</span> Español (Paraguay)
                      </button>
                      <button 
                        onClick={() => handleLanguageChange('es-AR')}
                        className={`w-full text-left px-3 py-1.5 hover:bg-slate-800 flex items-center gap-2 cursor-pointer ${i18n.language === 'es-AR' ? 'text-cyan-400 font-bold bg-slate-800/50' : ''}`}
                      >
                        <span>🇦🇷</span> Español (Argentina)
                      </button>
                      <button 
                        onClick={() => handleLanguageChange('es-CL')}
                        className={`w-full text-left px-3 py-1.5 hover:bg-slate-800 flex items-center gap-2 cursor-pointer ${i18n.language === 'es-CL' ? 'text-cyan-400 font-bold bg-slate-800/50' : ''}`}
                      >
                        <span>🇨🇱</span> Español (Chile)
                      </button>
                      <button 
                        onClick={() => handleLanguageChange('en-US')}
                        className={`w-full text-left px-3 py-1.5 hover:bg-slate-800 flex items-center gap-2 cursor-pointer ${i18n.language === 'en-US' ? 'text-cyan-400 font-bold bg-slate-800/50' : ''}`}
                      >
                        <span>🇺🇸</span> English (United States)
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Ciclo de Luminosidade (Galatea Signature): Claro / Escuro / Auto */}
            <button 
              onClick={() => setModePreference(nextModePref)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all duration-300 cursor-pointer border shadow-2xs bg-slate-800/90 border-slate-700 text-slate-200 hover:border-blue-400/50 hover:shadow-sm"
              title={modeButtonTitle}
            >
              {modePref === 'dark' ? (
                <>
                  <Moon className="w-4 h-4 text-sky-400 animate-pulse" />
                  <span className="font-semibold tracking-wide text-slate-200">Escuro</span>
                </>
              ) : modePref === 'auto' ? (
                <>
                  <Monitor className="w-4 h-4 text-emerald-400" />
                  <span className="font-semibold tracking-wide text-slate-100">Auto</span>
                </>
              ) : (
                <>
                  <Sun className="w-4 h-4 text-amber-400 animate-spin-slow" />
                  <span className="font-semibold tracking-wide text-slate-100">Claro</span>
                </>
              )}
            </button>

            <div className="relative cursor-pointer" title="Notificações">
              <MessageSquare className="w-5 h-5 hover:text-white transition-colors" />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-slate-950" />
            </div>

            <GalateaIAButton />
          </div>

          <div className="h-8 w-px bg-slate-700" />

          {/* Usuário */}
          <div className="flex items-center gap-3">
            <div className="flex flex-col text-right">
              <span className="text-xs font-bold text-slate-100">{nomeUsuario}</span>
              <span className="text-[10px] text-slate-400">{user?.email || ''}</span>
            </div>
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-sky-600 border border-slate-700 shadow-sm text-xs"
            >
              {inicialUsuario}
            </div>
            <button
              onClick={() => logout()}
              className="flex items-center gap-1 text-slate-400 hover:text-rose-400 ml-1 transition-colors cursor-pointer"
              title="Sair do Sistema"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
